import { useQuery } from '@tanstack/react-query';
import type { OrderChannel, OrderSource, PaymentType } from './types';
import { api } from './client';
import type { ReportSummary } from './types';

/**
 * Raporlar ekranı için tipler ve sorgu kancası.
 *
 * Bu dosya, `apps/panel/src/api/queries.ts#useReport` ile aynı ucu okur ama ek parametreler
 * (kaynak filtresi) taşır ve sunucudan gelen genişletilmiş yanıtı tamamen yansıtır. Mevcut
 * `ReportSummary` tipine dokunulmadığı için geriye dönük tüketiciler etkilenmez; bu
 * dosyadaki `ReportsResponse` tipi onun üstüne yeni alanları ekler.
 *
 * `queries.ts` ve `types.ts` ile çakışma: yeni tipler ve hook burada durur, eski tipler
 * yalnız `import type` ile okunur — kural gereği diğer dosyalara dokunulmaz.
 */

export type ReportSourceFilter = 'all' | 'abant' | 'hayat' | 'ours';

/** Damacana türü kırılımı: 19L PC = JUG_19L; 19L Cam = JUG_15L_GLASS; KOLI = CASE_05L. */
export interface JugKindBucket {
  '19L_PC': number;
  '19L_CAM': number;
  KOLI: number;
}

export interface JugsByKindReport {
  /** Bizim teslimattan gelen dolu + boş alımı. `emptyReceived` yalnız damacana türleri içindir. */
  ours: JugKindBucket & { emptyReceived: number };
  /** Panelde (HANDLED_EXTERNALLY) teslim edilen kalem toplamı. Boş alımı YOKTUR. */
  panel: JugKindBucket;
}

export interface VehicleOursRow {
  plate: string;
  delivered: number;
  revenue: string;
}

export interface VehiclePanelRow {
  label: string;
  source: OrderSource;
  delivered: number;
  cancelled: number;
  pending: number;
  revenue: string;
}

export interface VehicleBreakdownReport {
  ours: VehicleOursRow[];
  panel: VehiclePanelRow[];
}

export interface ProductSourceBreakdown {
  source: OrderSource;
  qty: number;
}

export interface ProductReportRow {
  productId: string;
  name: string;
  /** Tüm siparişlerdeki `OrderItem.qty` toplamı (iptal/işlemde dahil). */
  totalQty: number;
  /** Yalnız teslim edilenlerin `OrderItem.qty` toplamı. */
  deliveredQty: number;
  /** Yalnız teslim edilenlerin `lineTotal` toplamı (sunucuda `Decimal`, panel string okur). */
  revenue: string;
  /** Kaynak bazlı adet; toplamı `totalQty`'den büyük olamaz. */
  sources: ProductSourceBreakdown[];
}

export interface SourceChannelRow {
  source: OrderSource | 'TOTAL';
  delivered: number;
  cancelled: number;
  pending: number;
  revenue: string;
  channels: {
    channel: OrderChannel;
    delivered: number;
    cancelled: number;
    pending: number;
    revenue: string;
  }[];
}

export interface ReportsResponse extends ReportSummary {
  /** Aktif kaynak filtresi — ekranda hangi butonun seçili görüneceğini belirler. */
  sourceFilter: ReportSourceFilter;
  jugsByKind: JugsByKindReport;
  vehicles: VehicleBreakdownReport;
  productTable: ProductReportRow[];
  sourceChannelTable: SourceChannelRow[];
}

/**
 * Rapor sorgu kancası — dönem + kaynak filtreli.
 *
 * Kaynak filtresi ekrana ikinci bir segment olarak eklendi; veriyi sunucudan tek okumayla
 * taşıyacak şekilde sorgu anahtarına katılır. `source` değişince `queryKey` değişir,
 * react-query yeni isteği otomatik atar.
 */
export function useReport(period: string, source: ReportSourceFilter = 'all') {
  const params = new URLSearchParams();
  params.set('period', period);
  if (source !== 'all') params.set('source', source);
  const qs = params.toString();
  return useQuery({
    queryKey: ['report', qs],
    queryFn: () => api.get<ReportsResponse>(`/api/reports?${qs}`),
  });
}

/** Re-export: `PaymentType` zaten `types.ts`'te; burada yalnız taşımıyoruz. */
export type { PaymentType };
